import ShieldRounded from '@mui/icons-material/ShieldRounded';
import { Box, ButtonBase, Checkbox, Skeleton, Stack, Typography } from '@mui/material';
import { useAuth } from '../../../auth/AuthProvider';
import { useRoles } from '../api';

/** Multi-select of roles drawn as checkable rows. Only owners may hand out the Owner role. */
export function RolePicker({
  value,
  onChange,
  error,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  error?: string | undefined;
}) {
  const { me } = useAuth();
  const { data, isLoading } = useRoles();
  const roles = (data?.data ?? []).filter(
    (r) => me?.isOwner || !(r.isSystem && r.name === 'Owner') || value.includes(r.id),
  );
  const set = new Set(value);

  if (isLoading)
    return (
      <Stack spacing={1}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={52} />
        ))}
      </Stack>
    );

  return (
    <Box>
      <Box
        role="group"
        aria-label="Roles"
        sx={{
          border: 1,
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: '12px',
          overflow: 'hidden',
          maxHeight: 280,
          overflowY: 'auto',
        }}
      >
        {roles.map((r) => {
          const checked = set.has(r.id);
          return (
            <ButtonBase
              key={r.id}
              component="label"
              sx={(t) => {
                const p = (t.vars ?? t).palette.primary.main;
                return {
                  width: '100%',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  bgcolor: checked ? `color-mix(in srgb, ${p} 6%, transparent)` : 'transparent',
                  '& + &': { borderTop: 1, borderColor: 'divider' },
                  transition: 'background-color 120ms',
                };
              }}
            >
              <Checkbox
                checked={checked}
                onChange={(e) =>
                  onChange(e.target.checked ? [...value, r.id] : value.filter((id) => id !== r.id))
                }
                slotProps={{ input: { 'aria-label': r.name } }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                  <Typography sx={{ fontWeight: 600 }}>{r.name}</Typography>
                  {r.isSystem && <ShieldRounded sx={{ fontSize: 15, color: 'primary.main' }} />}
                </Stack>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {r.description ?? `${String(r.permissions.length)} permissions`}
                </Typography>
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
